import { inject, Injectable } from '@angular/core'
import { ActivatedRoute, NavigationBehaviorOptions, Params, Router } from '@angular/router'
import { forkJoin, from, Observable, of, throwError } from 'rxjs'
import { map, switchMap, take, tap } from 'rxjs/operators'
import { appConfig } from '../../../brands/_current/config/app-config'
import { WindowRefService } from '../../shared/globals-services/window-ref.service'
import { MyCiceroWebApiService } from '../../shared/mycicero-web-api/my-cicero-web-api.service'
import * as MyCiceroWebApiErrors from '../../shared/mycicero-web-api/shared/mycicero-web-api-errors.model'
import {
  BlacklistedAccount,
  BlacklistedIp,
  DisabledAccount,
  NoRedirectUrl,
  OtherLoginError,
  WrongCredentials,
} from '../../shared/mycicero-web-api/shared/mycicero-web-api-errors.model'
import { ErrorMessage } from '../login-form/message-error.enum'
import { CLIENT_PARAM, CLIENT_RETURN_URL_PARAM, SSO_RETURN_URL_PARAM } from './login-query-params'
import { mapRedirectOutput } from './login-redirect.model'
import { fromPromise } from 'rxjs/internal/observable/innerFrom'
import { fromSignUpPage } from '../../pending-operation/shared/pending-operation-params'
import { fromSignUpFinalizationPage } from '../../sign-up-finalization/shared/sign-up-finalization-params'
import { ExternalProviderEnum, ExternalProviderEnumArray } from '../external-provider/external-provider.enum'
import { crypto } from '@mycicero/spa-middleware-helper'
import { Location } from '@angular/common'
import {
  MOONEY_CHALLANGE_CODE_QUERY_PARAM,
  MOONEY_HMAC_QUERY_PARAM,
  MOONEY_REDIRECT_BACK_URL_QUERY_PARAM,
} from '../external-provider/external-provider-mooney.const'
import { STATE_QUERY_PARAM } from '../external-provider/external-login-back-redirect-page/external-login-back-redirect-page.component'
import { ExternalProviderState } from '../external-provider/external-provider-state.model'

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private readonly router = inject(Router)
  private readonly location = inject(Location)
  private readonly route = inject(ActivatedRoute)
  private readonly windowRefService = inject(WindowRefService)
  private readonly myCiceroWebApiService = inject(MyCiceroWebApiService)

  /**
   * In case of success, executes the redirect to complete the login, otherwise returns a {@link LoginError}
   * @param password - user password
   * @param redirectUrl - url for the redirect after login success
   * @param client - application client
   * @param recaptchaValue - value of the recaptcha
   * @param email - user email
   * @param mobileCountryCode - user mobile country code
   * @param mobileNumber - user mobile number
   */
  ssoLogin(
    password: string,
    redirectUrl: string,
    client: string,
    recaptchaValue: string,
    email?: string,
    mobileCountryCode?: string,
    mobileNumber?: string,
  ): Observable<true> {
    if (!redirectUrl) {
      return throwError(
        () => new MyCiceroWebApiErrors.NoRedirectUrl($localize`Impossibile accedere: redirect URL non specificato`),
      )
    }

    /**
     * The redirect to the Identity Server is executed by the {@see AccountStatusInterceptor}
     */
    return this.myCiceroWebApiService
      .ssoLogin(email, mobileCountryCode, mobileNumber, password, redirectUrl, client, recaptchaValue)
      .pipe(map(() => true))
  }

  /**
   * In case of success, executes the redirect to complete the login, otherwise returns a {@link LoginError}
   * @param email - user email
   * @param password - user password
   * @param redirectUrl - url for the redirect after login success
   * @param client - application client
   * @param recaptchaValue - value of the recaptcha
   * @param mobileCountryCode - user mobile country code
   * @param mobileNumber - user mobile number
   */
  clientLogin(
    email: string,
    password: string,
    redirectUrl: string,
    client: string,
    recaptchaValue: string,
    mobileCountryCode?: string,
    mobileNumber?: string,
  ) {
    if (!redirectUrl) {
      return throwError(
        () => new MyCiceroWebApiErrors.NoRedirectUrl($localize`Impossibile accedere: redirect URL non specificato`),
      )
    }

    if (!client) {
      return throwError(
        () => new MyCiceroWebApiErrors.NoClient($localize`Impossibile accedere: client non specificato`),
      )
    }

    return this.myCiceroWebApiService
      .clientLogin(
        email,
        mobileCountryCode,
        mobileNumber,
        password,
        decodeURIComponent(redirectUrl),
        client,
        recaptchaValue,
      )
      .pipe(
        map(mapRedirectOutput),
        map((loginRedirect) => {
          if (loginRedirect.redirectUrl) {
            window.window.location.replace(loginRedirect.redirectUrl)
            return true
          }
          throw new NoRedirectUrl($localize`Impossibile accedere: redirect URL non restituito dalle API`)
        }),
      )
  }

  /**
   * Handles login errors and returns an object representing the error type.
   * @param error The error thrown during the login attempt.
   * @returns An object with a key corresponding to the error type and value true.
   * The keys are defined in ErrorMessage and represent:
   * - badCredentials: wrong credentials
   * - disabled: disabled account
   * - blacklist: blacklisted account
   * - ipBlacklist: blacklisted IP
   * - other: other generic error
   */
  onLoginFail(error: unknown): { [key: number]: boolean } {
    switch (true) {
      case error instanceof WrongCredentials:
        return { [ErrorMessage.badCredentials]: true }
      case error instanceof DisabledAccount:
        return { [ErrorMessage.disabled]: true }
      case error instanceof BlacklistedAccount:
        return { [ErrorMessage.blacklist]: true }
      case error instanceof BlacklistedIp:
        return { [ErrorMessage.ipBlacklist]: true }
      case error instanceof OtherLoginError:
        return { [ErrorMessage.other]: true }
      default:
        return { [ErrorMessage.other]: true }
    }
  }

  /**
   * Handles the redirect to the correct login page based on query string parameters.
   * Returns an Observable<boolean> indicating if the redirect was performed.
   * @param [options] Optional navigation behavior options for Angular Router.
   * @returns True if redirected, false otherwise.
   */
  backToLogin(options?: NavigationBehaviorOptions): Observable<boolean> {
    return this.route.queryParamMap.pipe(
      take(1),
      switchMap((queryParamMap) => {
        const client = queryParamMap.get(CLIENT_PARAM)
        const redirect = queryParamMap.get(CLIENT_RETURN_URL_PARAM)
        const returnUrl = queryParamMap.get(SSO_RETURN_URL_PARAM)

        /**
         * If we are redirecting to the login page, then we are no longer from the sign-up flow.
         */
        const removeFromSignUp = { [fromSignUpPage]: null, [fromSignUpFinalizationPage]: null }

        if (returnUrl) {
          return fromPromise<boolean>(
            this.router.navigate([appConfig.login.paths._root, appConfig.login.paths.ssoLogin], {
              queryParamsHandling: 'merge',
              queryParams: { ...removeFromSignUp },
              ...options,
            }),
          )
        } else if (client && redirect) {
          return fromPromise<boolean>(
            this.router.navigate([appConfig.login.paths._root, appConfig.login.paths.clientLogin], {
              queryParamsHandling: 'merge',
              queryParams: { ...removeFromSignUp },
              ...options,
            }),
          )
        } else if (redirect) {
          // Only redirect is present, send user directly to redirect without passing through login
          this.windowRefService.window.location.replace(redirect)
          return of(true)
        }

        return of(false)
      }),
    )
  }

  /**
   * Returns the external provider login URL with optional PKCE challenge parameters.
   *
   * This function constructs a login URL for an external authentication provider.
   * If the provider is enabled and uses PKCE (Proof Key for Code Exchange), it appends
   * the `code_challenge_method` and `code_challenge` to the URL. Otherwise, it returns
   * a basic login URL with a `returnUrl` parameter.
   * @param provider - The external authentication provider enum key.
   * @returns An observable that emits the constructed login URL.
   */
  getExternalProviderUrl(provider: ExternalProviderEnum): Observable<string> {
    const { challengeMethod = 'none' } = appConfig.login.externalProvider.providers[provider]
    const stateKey = challengeMethod !== 'none' ? crypto.createRandomString(36) : null
    const queryParams: Params = stateKey ? { [STATE_QUERY_PARAM]: stateKey } : {}
    const redirectUrl = this.prepareExternalUrl(
      ['/', appConfig.login.paths._root, appConfig.login.externalProvider.paths._root, provider.toLowerCase()],
      queryParams,
    )

    if (provider === ExternalProviderEnumArray[2]) {
      return this.getMooneyExternalLoginUrl(redirectUrl, stateKey)
    }

    return throwError(() => new Error('No Valid External Login Provider'))
  }

  /**
   * Checks the external login by verifying the authorization code and PKCE verifier.
   * @param provider The external authentication provider.
   * @param verifierCode The PKCE code verifier.
   * @param authorizationCode The authorization code from the provider.
   * @param returnUrl The URL to redirect after login.
   * @param recaptchaValue The recaptcha value for validation.
   * @returns True if login is successful.
   */
  checkExternalLogin(
    provider: ExternalProviderEnum,
    verifierCode: string,
    authorizationCode: string,
    returnUrl: string,
    recaptchaValue: string,
  ): Observable<true> {
    return this.myCiceroWebApiService
      .externalLogin(
        {
          LoginClient: appConfig.login.singleSignOn.clientName,
          LoginProvider: provider,
          LoginRedirect: returnUrl,
          AuthorizationCode: authorizationCode,
          CodeVerifier: verifierCode,
        },
        recaptchaValue,
      )
      .pipe(map(() => true))
  }

  /**
   * Prepares an external URL for authentication providers, including query parameters.
   * @param path Array of path segments for the URL.
   * @param [queryParams] Optional query parameters to include.
   * @returns The normalized external URL as a string.
   */
  private prepareExternalUrl(path: string[], queryParams?: Params): string {
    const urlTree = this.router.createUrlTree(path, { queryParams })
    const serializedUrl = this.router.serializeUrl(urlTree)
    const externalUrl = this.location.prepareExternalUrl(serializedUrl)

    return this.location.normalize(`${origin}${externalUrl}`)
  }

  /**
   * Generates the external login URL for Mooney provider, including PKCE and HMAC parameters if required.
   * @param redirectUrl The redirect URL after login.
   * @param stateKey Optional PKCE state key.
   * @returns Observable<string> The constructed Mooney login URL.
   */
  private getMooneyExternalLoginUrl(redirectUrl: string, stateKey?: string): Observable<string> {
    const { loginURL = '' } = appConfig.login.externalProvider.providers[ExternalProviderEnumArray[2]]
    const url = new URL(loginURL)
    const pkce$ = stateKey ? from(crypto.generateCodeChallenge()) : of(null)
    const hmac$ = this.myCiceroWebApiService
      .generateHmacSignature(redirectUrl, ExternalProviderEnumArray[2])
      .pipe(map((res) => res.SignedUrlRedirect))

    url.searchParams.set(MOONEY_REDIRECT_BACK_URL_QUERY_PARAM, redirectUrl)

    return forkJoin({ pkce: pkce$, hmac: hmac$ }).pipe(
      tap(({ pkce, hmac }) => {
        if (pkce && stateKey) {
          const state: ExternalProviderState = {
            ...pkce,
            returnUrl: this.route.snapshot.queryParamMap.get(SSO_RETURN_URL_PARAM),
          }
          window.sessionStorage.setItem(stateKey, JSON.stringify(state))
          url.searchParams.set(MOONEY_CHALLANGE_CODE_QUERY_PARAM, pkce.challenge)
        }

        url.searchParams.set(MOONEY_HMAC_QUERY_PARAM, hmac)
      }),
      map(() => url.toString()),
    )
  }
}
